'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';

import type { Reference } from '@eliadmin/core/src/api/references/references-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { LightboxComponent } from '@eliadmin/core/src/components/lightbox/lightbox-component';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useState } from 'react';

export function ReferenceCard({
  reference,
  inCarousel = false,
}: {
  reference: Reference;
  inCarousel?: boolean;
}) {
  const allImages = [reference.image, ...(reference.images || [])];

  const { isOpen, setIsOpen } = useDisclosure(false);
  const [imagesSorted, setImagesSorted] = useState<{ url: string; name: string }[]>([]);

  const handleOpenGallery = (images: any, index: number) => () => {
    if (Array.isArray(images) && index >= 0 && index < images.length) {
      const newImages = [...images.slice(index), ...images.slice(0, index)];
      setImagesSorted(newImages);
      setIsOpen(true);
    } else if (images && !Array.isArray(images)) {
      setImagesSorted([images]);
      setIsOpen(true);
    }
  };

  return (
    <>
      <LightboxComponent isOpen={isOpen} setIsOpen={setIsOpen} images={imagesSorted} />
      <div
        onClick={handleOpenGallery(allImages, 0)}
        className={cn(
          'group relative col-span-1 flex w-full cursor-pointer flex-col items-start justify-between overflow-hidden rounded-[8px] p-4 md:p-5 xl:p-6',
          inCarousel
            ? 'h-[230px] sm:h-[270px] md:h-[320px] lg:h-[390px] xl:h-[450px]'
            : 'h-[250px] sm:h-[300px] md:h-[350px] lg:h-[420px] xl:h-[440px]',
        )}
      >
        {!!reference.year && (
          <p className="relative z-10 px-3 py-1 text-[10px] text-white sm:px-4 sm:py-2 sm:text-xs md:text-sm lg:text-base xl:text-xl">
            {reference.year}
          </p>
        )}
        <div className="relative z-10 flex w-full flex-col items-start justify-start gap-1 sm:gap-2">
          <h4 className="text-sm font-semibold leading-[1.2] tracking-tighter text-white sm:text-base sm:leading-[1.2] md:text-2xl md:leading-[1.2] lg:text-3xl lg:leading-[1.2] xl:text-[44px] xl:leading-[1.2]">
            {reference.name}
          </h4>
          {reference.categories && reference.categories.length > 0 && (
            <p className="text-sm font-semibold leading-[1.2] text-white sm:text-base sm:leading-[1.2] md:text-lg md:leading-[1.2] lg:text-xl lg:leading-[1.2] xl:text-2xl xl:leading-[1.2]">
              {reference.categories[0]?.name}
            </p>
          )}
        </div>
        <div
          className={cn(
            'absolute inset-0 z-[1] h-full w-full bg-black/30 transition-all',
            'group-hover:bg-black/50',
          )}
        />
        <Image
          src={reference?.image?.url ?? '/images/placeholder.png'}
          alt={resolveImageAlt(reference?.image, reference?.name)}
          width={0}
          height={0}
          className="absolute inset-0 z-0 h-full w-full object-cover object-center"
          sizes="100vw"
        />
      </div>
    </>
  );
}
